import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import store from "../redux/store/store";
import {apiLoading} from "../redux/slice/apiSlice";
import {objectTypeChecker} from "../components/commonmethod/CommonMethod";


/** JsGrid `onPageChange` pageable → API용 sort/desc */
function parseJsGridPageable(pageable, prev) {
    const pageNumber = pageable?.pageNumber ?? prev?.currentPage ?? 0;
    const size = pageable?.pageSize ?? pageable?.size ?? prev?.size ?? 20;
    let sort = prev?.sort ?? 'createdAt';
    let desc = prev?.desc ?? true;

    const sortArr = pageable?.sort;
    const token = Array.isArray(sortArr) && sortArr[0] != null ? String(sortArr[0]) : null;
    if (token) {
        const comma = token.indexOf(',');
        if (comma >= 0) {
            sort = token.slice(0, comma).trim();
            desc = token.slice(comma + 1).trim().toLowerCase() === 'desc';
        } else {
            sort = token.trim();
            if (pageable?.sortDirection != null) {
                desc = String(pageable.sortDirection).toUpperCase() === 'DESC';
            }
        }
    } else if (pageable?.sortDirection != null) {
        desc = String(pageable.sortDirection).toUpperCase() === 'DESC';
    }

    return { pageNumber, size, sort, desc };
}

/** @param {import('./useRequestData.types').Page} pageInfo */
function buildPageable(pageInfo, apiPageable) {
    const sortField = pageInfo?.sort;
    const sortToken =
        sortField != null && sortField !== ''
            ? `${sortField},${pageInfo?.desc ? 'desc' : 'asc'}`
            : null;
    return {
        ...(apiPageable || {}),
        pageNumber: pageInfo?.currentPage ?? apiPageable?.pageNumber,
        size: pageInfo?.size ?? apiPageable?.pageSize ?? apiPageable?.size,
        pageSize: pageInfo?.size ?? apiPageable?.pageSize ?? apiPageable?.size,
        sort: sortToken ? [sortToken] : apiPageable?.sort,
        desc: pageInfo?.desc,
        totalElements: pageInfo?.totalElements ?? apiPageable?.totalElements,
        totalPages: pageInfo?.totalPages ?? apiPageable?.totalPages,
        sortDirection: pageInfo?.desc ? 'DESC' : 'ASC',
    };
}

function deleteEntitySearchToLocalStorage(entity) {
    const savedData = localStorage.getItem("savedSearch");
    if (!savedData) return
    let parsedData = JSON.parse(savedData)
    if (parsedData.hasOwnProperty(entity)) {
        delete parsedData[entity];
    }
    localStorage.setItem("savedSearch", JSON.stringify(parsedData))
}

const useRequestData = (fetchDataMethod, initDate, entity) => {


    function setSearchToLocalStorage(updatedConditions, pageInfo, entity) {
        if(entity) {
            const existingData = localStorage.getItem("savedSearch");
            let parsedData = existingData ? JSON.parse(existingData) : {};

            parsedData[entity] = {
                ...updatedConditions,
                page: pageInfo.currentPage,
                size: pageInfo.size,
                sort: pageInfo.sort,
                desc: pageInfo.desc,
            };
            setSearchConditions(parsedData[entity]);
            localStorage.setItem("savedSearch", JSON.stringify(parsedData));
        }
    }


    const [searchConditions, setSearchConditions] = useState(
        JSON.parse(localStorage.getItem("savedSearch"))?.[entity]
        ??
        {
        searchTxt:'',
        ...(initDate && {
            startDate: initDate.startDate,
            endDate: initDate.endDate
        }),
    });

    const [data, setData] = useState([]);
    const [apiPageable, setApiPageable] = useState(null);


    const [pageInfo, setPageInfo] = useState(
        { currentPage: JSON.parse(localStorage.getItem("savedSearch"))?.[entity]?.page ?? 0, totalPages: 0, size: JSON.parse(localStorage.getItem("savedSearch"))?.[entity]?.size ?? 20, sort: JSON.parse(localStorage.getItem("savedSearch"))?.[entity]?.sort ?? 'createdAt', desc: JSON.parse(localStorage.getItem("savedSearch"))?.[entity]?.desc ?? true }
    );
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const fetchGenerationRef = useRef(0);

    const fetchData = useCallback(async (conditions, page, size, sort, desc) => {
        const generation = ++fetchGenerationRef.current;
        setLoading(true);
        try {
            const pageable = { page, size, sort, desc };
            const response = await fetchDataMethod(conditions, pageable);
            if (generation !== fetchGenerationRef.current) return;

            const responseData = response?.data;
            if (!responseData || !Array.isArray(responseData.content)) {
                setData([]);
                setApiPageable(responseData?.pageable ?? null);
                setPageInfo((prev) => ({
                    ...prev,
                    totalElements: responseData?.totalElements ?? 0,
                    totalPages: responseData?.totalPages ?? 0,
                }));
                return;
            }

            const currentPage = responseData.pageable?.pageNumber ?? page;
            const totalElements = responseData.totalElements ?? 0;
            const totalPages = responseData.totalPages ?? 0;

            if (
                responseData.content.length === 0 &&
                totalElements > 0 &&
                currentPage > 0 &&
                currentPage >= Math.max(totalPages - 1, 0)
            ) {
                const resetPageInfo = {
                    currentPage: 0,
                    totalElements,
                    totalPages,
                    size,
                    sort,
                    desc,
                };
                setPageInfo(resetPageInfo);
                setSearchToLocalStorage(conditions, resetPageInfo, entity);
                fetchData(conditions, 0, size, sort, desc);
                return;
            }

            setData(responseData.content);
            setApiPageable(responseData.pageable ?? null);
            setPageInfo({
                currentPage,
                totalElements,
                totalPages,
                size,
                sort,
                desc,
            });
        } catch (err) {
        } finally {
            if (generation === fetchGenerationRef.current) {
                setLoading(false);
            }
        }
    }, [fetchDataMethod]);

    useEffect(() => {
        const savedSearch = JSON.parse(localStorage.getItem("savedSearch"));

        if(entity && savedSearch && savedSearch[entity]) {
            fetchData(searchConditions, pageInfo.currentPage, pageInfo.size, pageInfo.sort, pageInfo.desc);
        }else{
            fetchData(searchConditions, pageInfo.currentPage, pageInfo.size, pageInfo.sort, pageInfo.desc);
        }
    }, []);

    const handleSearch = (newConditions) => {
        // const nonObjectConditions = {};
        // const objectConditions = {};
        //
        // Object.keys(searchConditions).forEach((key) => {
        //     const value = searchConditions[key];
        //     if (objectTypeChecker(value)) {
        //         // 객체 및 배열로 변경
        //         objectConditions[key] = value;
        //     } else {
        //         // null 또는 기본 타입 (문자열, 숫자, 불리언) 또는 배열은 nonObjectConditions에 포함
        //         nonObjectConditions[key] = value;
        //     }
        // });
        //
        // let updatedConditions = nonObjectConditions;
        //
        // Object.keys(newConditions).forEach((key) => {
        //     const value = newConditions[key]
        //     if (objectTypeChecker(value)) {
        //         // 객체 및 배열로 변경
        //         updatedConditions = {
        //             ...nonObjectConditions,
        //             ...newConditions
        //         }
        //     } else {
        //
        //
        //         // null 또는 기본 타입 (문자열, 숫자, 불리언) 또는 배열은 nonObjectConditions에 포함
        //         updatedConditions = {
        //             // ...searchConditions,
        //             ...objectConditions,
        //             ...newConditions
        //         }
        //     }
        // });

        const merged = {...searchConditions};
        Object.entries(newConditions).forEach(([key, value]) => {
            if (value === null) {
                delete merged[key];
            } else {
                merged[key] = value;
            }
        });
        setPageInfo((prev) => ({ ...prev, currentPage: 0 }));
        setSearchToLocalStorage(merged, pageInfo, entity);
        fetchData(merged, 0, pageInfo.size, pageInfo.sort, pageInfo.desc);
    };



    const handlePageChange = (newPage) => {
        setPageInfo((prev) => {
            const updatedPageInfo = { ...prev, currentPage: newPage };
            setSearchToLocalStorage(searchConditions, updatedPageInfo, entity);
            fetchData(searchConditions, newPage, updatedPageInfo.size, updatedPageInfo.sort, updatedPageInfo.desc);
            return updatedPageInfo;
        });
    };

    /** JsGrid `onPageChange` — pageNumber + sort 모두 반영 */
    const handleGridPageChange = (pageable) => {
        setPageInfo((prev) => {
            const { pageNumber, size, sort, desc } = parseJsGridPageable(pageable, prev);
            const updatedPageInfo = { ...prev, currentPage: pageNumber, size, sort, desc };
            setSearchToLocalStorage(searchConditions, updatedPageInfo, entity);
            fetchData(searchConditions, pageNumber, size, sort, desc);
            return updatedPageInfo;
        });
    };

    const handleSortChange = (newPage) => {
        setPageInfo((prev) => {
            const updatedPageInfo = {
                ...prev,
                sort: newPage,  // sort 값을 newPage로 설정
                desc: !prev.desc // desc 값을 반전시킴
            };
            setSearchToLocalStorage(searchConditions, updatedPageInfo, entity);
            fetchData(searchConditions, 0, updatedPageInfo.size, updatedPageInfo.sort, updatedPageInfo.desc);
            return updatedPageInfo;
        });
    };

    const handlePageSizeChange = (newSize) => {
        setPageInfo(prev => {
            const updatedPageInfo = { ...prev, size: newSize, currentPage: 0 };
            setSearchToLocalStorage(searchConditions, updatedPageInfo, entity);
            fetchData(searchConditions, 0, updatedPageInfo.size, updatedPageInfo.sort, updatedPageInfo.desc);
            return updatedPageInfo;
        });
    };

    const getQr =(checked)=> {
        if(checked?.checked && checked?.checked.length < 1){
            alert('자산을 선택하세요')
            return;
        }
        const list = data.filter(e=>checked.checked.includes(e.id))
        localStorage.setItem('qrList', JSON.stringify(list));
        const qrPopup = window.open(`/qrcode`, "_blank", 'width=600,height=800');
    }

    const reload = () => {
        fetchData(
            searchConditions,
            pageInfo.currentPage,
            pageInfo.size,
            pageInfo.sort,
            pageInfo.desc,
        );
    };

    const refresh = () => {
        const init=  {
            searchTxt:'',
            ...(initDate && {
                startDate: initDate.startDate,
                endDate: initDate.endDate
            })}
        const resetPageInfo = {
            currentPage: 0,
            totalPages: 0,
            size: 20,
            sort: 'createdAt',
            desc: true,
        };
        setSearchConditions(init);
        setPageInfo(resetPageInfo);
        deleteEntitySearchToLocalStorage(entity);
        fetchData(init, 0, resetPageInfo.size, resetPageInfo.sort, resetPageInfo.desc);
    };

    function getSearchToLocalStorage(entity) {
        const getItem = localStorage.getItem("savedSearch")
        let result  = searchConditions
        if(entity && JSON.parse(getItem) && JSON.parse(getItem)[entity]) result = JSON.parse(getItem)[entity];
        return result;
    }

    const testSearch =  getSearchToLocalStorage(entity)

    const dataHook={
        handleSearch:handleSearch,
        handlePageChange:handlePageChange,
        handleGridPageChange:handleGridPageChange,
        handlePageSizeChange:handlePageSizeChange,
        searchConditions:searchConditions,
        handleSortChange:handleSortChange,
        refresh:refresh,
        reload:reload,
        saveConditions:testSearch,
        loading,
    };

    /** @type {import('./useRequestData.types').Data} */
    const newData = useMemo(() => ({
        content: data,
        pageable: buildPageable(pageInfo, apiPageable),
        totalElements: pageInfo.totalElements,
        totalPages: pageInfo.totalPages,
    }), [data, pageInfo, apiPageable]);

    return { data, newData, pageInfo, dataHook, getQr, loading };
};

export default useRequestData;
